'use client';

import { ReactNode } from 'react';
import { useI18n } from '@fshr/context/i18n-context';
import { cn } from '@fshr/shared-react';
import { useMediaQuery } from 'react-responsive';
import BevSafeText from '../bev-safe-text';
import Button from '../button';

export type SummaryItemSpan = 1 | 2 | 3 | 4;

export type SummaryItem = {
	/** Optional label above the value. */
	title?: string;
	value: string;
	/** Column span within the subsection grid. Mobile is always full width. */
	span?: SummaryItemSpan;
};

export type SummarySubsection = {
	/** Optional heading for a group of items within the section. */
	title?: ReactNode;
	items: SummaryItem[];
	/** Fields per row on desktop. Default inherits from section, then 2. */
	columns?: 1 | 2 | 3 | 4;
};

export type SummarySectionProps = {
	/** Section header (usually the step name). */
	title?: ReactNode;
	/** Mobile/tablet only: returns to the related step. */
	onEditClick?: () => void;
	subsections: SummarySubsection[];
	/** Default columns for subsections that omit `columns`. Default 2. */
	columns?: 1 | 2 | 3 | 4;
};

const spanClassName: Record<SummaryItemSpan, string> = {
	1: 'md:col-span-1',
	2: 'md:col-span-2',
	3: 'md:col-span-3',
	4: 'md:col-span-4',
};

const columnsClassName: Record<1 | 2 | 3 | 4, string> = {
	1: 'md:grid-cols-1',
	2: 'md:grid-cols-2',
	3: 'md:grid-cols-3',
	4: 'md:grid-cols-4',
};

function hasContent(node: ReactNode | undefined): boolean {
	if (node === undefined || node === null || node === false) return false;
	if (typeof node === 'string') return node.trim().length > 0;
	return true;
}

/**
 * One step's summary card: title (+ Edit on mobile/tablet), subsections, label/value grid.
 */
export const SummarySection = ({
	title,
	onEditClick,
	subsections,
	columns = 2,
}: SummarySectionProps) => {
	const { t } = useI18n();
	const isDesktop = useMediaQuery({ minWidth: 960 });
	const showTitle = hasContent(title);
	const showEdit = Boolean(onEditClick) && !isDesktop;

	return (
		<div className="flex w-full flex-col overflow-hidden border border-border-subtle">
			{showTitle || showEdit ? (
				<div className="flex w-full flex-row items-center justify-between gap-[10px] bg-surface-raised-subtle spacing-card-padding-x-s spacing-card-padding-y-xs">
					{showTitle ? (
						typeof title === 'string' || typeof title === 'number' ? (
							<span className="text-subtitle2 text-text-heading">
								<BevSafeText>{title}</BevSafeText>
							</span>
						) : (
							title
						)
					) : (
						<span />
					)}
					{showEdit ? (
						<Button
							type="button"
							intent="text"
							size="small"
							onClick={onEditClick}
							className="shrink-0"
						>
							{t('Edit')}
						</Button>
					) : null}
				</div>
			) : null}

			<div className="flex w-full flex-col spacing-stack-l spacing-card-padding-x-s pt-[15px] md:pt-[20px] pb-[15px] md:pb-[20px]">
				{subsections.map((subsection, subsectionIndex) => {
					const subsectionColumns = subsection.columns ?? columns;
					const showSubsectionTitle = hasContent(subsection.title);

					return (
						<div key={subsectionIndex} className="flex w-full flex-col spacing-stack-s">
							{showSubsectionTitle ? (
								typeof subsection.title === 'string' || typeof subsection.title === 'number' ? (
									<span className="text-subtitle2 text-text-heading">
										<BevSafeText>{subsection.title}</BevSafeText>
									</span>
								) : (
									subsection.title
								)
							) : null}

							<div
								className={cn(
									'grid w-full grid-cols-1 gap-x-[30px] gap-y-[20px]',
									columnsClassName[subsectionColumns]
								)}
							>
								{subsection.items.map(({ title: itemTitle, value, span }, itemIndex) => (
									<div
										key={`${itemTitle ?? 'item'}-${itemIndex}`}
										className={cn(
											'col-span-1 flex min-w-0 flex-col gap-[5px]',
											span ? spanClassName[span] : undefined
										)}
									>
										{hasContent(itemTitle) ? (
											<span className="text-label text-text-secondary">{itemTitle}</span>
										) : null}
										<span className="break-words text-body2 text-text-primary">{value || '—'}</span>
									</div>
								))}
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
};
